/**
 * PromotionCreditReminderModal — daily nudge for users holding unused
 * weekly-winner promotion credit.
 *
 * Rules (all enforced client-side via localStorage):
 *   • Only shown if `/api/promotion-credits/balance` returns balance > 0.
 *   • Shown at most ONCE per calendar day, keyed by local date string.
 *   • Dismissed manually OR auto-dismisses after navigating away.
 *   • Stops showing entirely once the user fully spends the credit.
 *
 * Explains:
 *   • What promotion credit is (free winner reward, spend it on boosts)
 *   • Days left until credit expires
 *   • How to USE it (auto-applies at checkout on Promote)
 *   • Full tier price table (Basic / Standard / Premium / Ultimate)
 *   • CTA to Promote page + "Close for today"
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Sparkles, Clock, TrendingUp, Zap, Music, Video, User, Calendar, ArrowRight, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LS_KEY = 'ovd_credit_reminder_last_shown';

const TIERS = [
  { key: 'basic',    name: 'Basic',    price: 10, days: 1, max: 1, gradient: 'from-[#00CCFF] to-[#00aaff]', icon: Zap },
  { key: 'standard', name: 'Standard', price: 25, days: 3, max: 1, gradient: 'from-[#00ff88] to-[#00aa66]', icon: TrendingUp },
  { key: 'premium',  name: 'Premium',  price: 60, days: 7, max: 1, gradient: 'from-[#FFDA33] to-[#ff8c00]', icon: Sparkles },
  { key: 'ultimate', name: 'Ultimate', price: 250, days: 30, max: 3, gradient: 'from-[#FF4D8B] to-[#9945FF]', icon: Sparkles },
];

const ICONS_BY_TYPE = [
  { Icon: Music, label: 'Tracks' },
  { Icon: Video, label: 'Videos' },
  { Icon: User, label: 'Profile' },
  { Icon: Calendar, label: 'Events' },
];

function daysUntil(iso) {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / 86400000));
}

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

export default function PromotionCreditReminderModal() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [credit, setCredit] = useState({ balance: 0, next_expiry: null });

  useEffect(() => {
    if (!token || !user) return;
    // Only show for creator accounts (same gate as Promote eligibility)
    const isCreator = user.is_artist || user.is_producer || user.is_ai_artist
      || user.is_dj || user.is_promoter || user.is_admin;
    if (!isCreator) return;
    // Once-per-day throttle
    const lastShown = localStorage.getItem(LS_KEY);
    if (lastShown === today()) return;

    let cancelled = false;
    axios.get(`${API}/promotion-credits/balance`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (cancelled) return;
        const data = r.data || {};
        if ((data.balance || 0) > 0) {
          setCredit(data);
          // Small delay so it feels less aggressive on page load
          setTimeout(() => setOpen(true), 1200);
        }
      })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [token, user]);

  const dismiss = () => {
    localStorage.setItem(LS_KEY, today());
    setOpen(false);
  };

  const gotoPromote = () => {
    localStorage.setItem(LS_KEY, today());
    setOpen(false);
    navigate('/promote');
  };

  if (!open) return null;

  const daysLeft = daysUntil(credit.next_expiry);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent
        className="max-w-lg bg-[#070707] border border-[#FFDA33]/30 text-white p-0 overflow-hidden"
        data-testid="credit-reminder-modal"
      >
        {/* Header strip */}
        <div className="relative px-6 pt-6 pb-4 bg-gradient-to-br from-[#FFDA33]/15 via-[#00ff88]/8 to-transparent">
          <button
            onClick={dismiss}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/15 hover:bg-white/30 active:bg-white/45 flex items-center justify-center text-white border border-white/30 hover:border-white shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFDA33] focus-visible:ring-offset-2 focus-visible:ring-offset-black transition-colors"
            data-testid="credit-reminder-close"
            aria-label="Close promotion credit reminder"
            type="button"
          >
            <X size={18} strokeWidth={2.5} />
          </button>
          <DialogHeader>
            <DialogTitle className="text-white font-['Chakra_Petch'] text-2xl flex items-center gap-2">
              <Sparkles size={22} className="text-[#FFDA33]" />
              You have ${credit.balance.toFixed(2)} in promotion credit
            </DialogTitle>
            <DialogDescription className="sr-only">
              You have unused promotion credit. Use it to boost any of your tracks, videos, events, or profile within {daysLeft ?? 'a few'} days. Press Escape or use the close button to dismiss this reminder.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-3 flex items-center gap-2 text-sm font-mono">
            <Clock size={14} className="text-[#FFDA33]" />
            <span className="text-white">
              {daysLeft !== null
                ? <><strong className="text-[#FFDA33]">{daysLeft}</strong> day{daysLeft === 1 ? '' : 's'} left to use it</>
                : 'Available now'}
            </span>
          </div>
        </div>

        <div className="px-6 pb-5 pt-2 max-h-[70vh] overflow-y-auto">
          {/* How it works */}
          <section>
            <h3 className="text-[11px] font-mono uppercase tracking-widest text-[#FFDA33] mb-2">
              How to use your credit
            </h3>
            <ol className="space-y-2 text-sm text-white/85 font-sans">
              <li className="flex gap-2">
                <span className="w-5 h-5 shrink-0 rounded-full bg-[#FFDA33]/20 text-[#FFDA33] font-bold text-xs flex items-center justify-center">1</span>
                Open any of your tracks, videos, profile, or events and hit <strong className="text-white">"Promote"</strong>.
              </li>
              <li className="flex gap-2">
                <span className="w-5 h-5 shrink-0 rounded-full bg-[#FFDA33]/20 text-[#FFDA33] font-bold text-xs flex items-center justify-center">2</span>
                Choose a tier. Your credit <strong className="text-[#00ff88]">auto-applies at checkout</strong> — no coupon code needed.
              </li>
              <li className="flex gap-2">
                <span className="w-5 h-5 shrink-0 rounded-full bg-[#FFDA33]/20 text-[#FFDA33] font-bold text-xs flex items-center justify-center">3</span>
                If the tier costs ≤ your credit, you pay <strong className="text-white">$0</strong>. If higher, only the difference charges.
              </li>
              <li className="flex gap-2">
                <span className="w-5 h-5 shrink-0 rounded-full bg-[#FFDA33]/20 text-[#FFDA33] font-bold text-xs flex items-center justify-center">4</span>
                Unused credit expires in {daysLeft ?? 90} days — use it or lose it.
              </li>
            </ol>
          </section>

          {/* What you can promote */}
          <section className="mt-5">
            <h3 className="text-[11px] font-mono uppercase tracking-widest text-[#FFDA33] mb-2">
              What you can promote
            </h3>
            <div className="grid grid-cols-4 gap-2">
              {ICONS_BY_TYPE.map(({ Icon, label }) => (
                <div key={label} className="flex flex-col items-center gap-1 p-2 rounded border border-white/5 bg-white/[0.03]">
                  <Icon size={16} className="text-[#00CCFF]" />
                  <span className="text-[10px] font-mono text-white/70 uppercase tracking-widest">{label}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Tier prices — buy more */}
          <section className="mt-5">
            <h3 className="text-[11px] font-mono uppercase tracking-widest text-[#FFDA33] mb-2">
              Want to promote more? Tier pricing
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {TIERS.map((t) => {
                const TierIcon = t.icon;
                const covered = credit.balance >= t.price;
                return (
                  <div
                    key={t.key}
                    className={`relative rounded-lg border p-3 bg-[#0a0a0a] ${
                      covered ? 'border-[#00ff88]/50' : 'border-white/10'
                    }`}
                    data-testid={`credit-reminder-tier-${t.key}`}
                  >
                    {covered && (
                      <span className="absolute top-1.5 right-1.5 text-[9px] font-mono uppercase tracking-widest text-[#00ff88] bg-[#00ff88]/15 border border-[#00ff88]/40 rounded px-1.5 py-0.5">
                        FREE
                      </span>
                    )}
                    <div className={`w-7 h-7 rounded bg-gradient-to-br ${t.gradient} flex items-center justify-center mb-2`}>
                      <TierIcon size={14} className="text-black" />
                    </div>
                    <div className="text-white font-['Chakra_Petch'] text-lg leading-none">
                      ${t.price}
                    </div>
                    <div className="text-[10px] font-mono text-white/60 uppercase tracking-widest mt-0.5">
                      {t.name}
                    </div>
                    <div className="text-[10px] font-mono text-white/40 mt-1">
                      {t.days}d · up to {t.max} item{t.max > 1 ? 's' : ''}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] font-mono text-white/50 mt-2">
              Buy additional promotions anytime via Stripe — your winner credit covers part of the cost automatically.
            </p>
          </section>

          {/* CTAs */}
          <div className="flex gap-2 mt-6">
            <Button
              onClick={gotoPromote}
              className="flex-1 bg-[#FFDA33] text-black hover:bg-[#FFE97A] font-['Chakra_Petch'] uppercase tracking-wider"
              data-testid="credit-reminder-go-promote"
            >
              Start promoting <ArrowRight size={14} className="ml-1.5" />
            </Button>
            <Button
              onClick={dismiss}
              variant="outline"
              className="border-white/20 text-white/80 hover:bg-white/5 font-mono uppercase tracking-widest text-xs"
              data-testid="credit-reminder-dismiss"
            >
              Remind me tomorrow
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
